import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as StatusIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';

  try {
    const date = new Date(dateTimeString);

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateTimeString;
  }
};
const CityViewModal = ({ open, onClose, city }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!city) return null;

  // Function to safely render HTML content
  const renderHTML = (htmlString) => {
    return { __html: htmlString || 'No description available' };
  };

  // Info Row Component
  const InfoRow = ({ icon, label, value, chip }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
        {icon}
        <Typography
          sx={{
            ml: 1.5,
            fontFamily: 'poppinsMedium',
            color: palette.text.secondary,
            fontSize: '14px'
          }}
        >
          {label}:
        </Typography>
      </Box>
      {chip ? (
        <Chip
          label={value}
          color={value === 'active' ? 'success' : 'default'}
          size="small"
          sx={{ fontFamily: 'poppinsMedium' }}
        />
      ) : (
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.text.primary,
            flex: 1,
            fontSize: '14px'
          }}
        >
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          margin: isMobile ? 0 : { xs: 1, sm: 3 },
          width: '100%',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2.5,
          position: 'relative',
          borderTopLeftRadius: isMobile ? 0 : 8,
          borderTopRightRadius: isMobile ? 0 : 8
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText,
            fontSize: '20px'
          }}
        >
          City Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: palette.secondary.contrastText,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            },
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        {/* City Image */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 200,
              height: 150,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <img
              src={city.image}
              alt={city.city}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: palette.grey[300]
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No Image
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Basic Information */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 3,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LocationIcon fontSize="small" />
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {/* City Name */}
              <InfoRow
                icon={<LocationIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="City Name"
                value={city?.city?.name}
              />

              {/* State */}
              <InfoRow
                icon={<LocationIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="State"
                value={city?.state?.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Slug */}
              <InfoRow
                icon={<DescriptionIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Slug"
                value={city.slug}
              />

              {/* Status */}
              <InfoRow
                icon={<StatusIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Status"
                value={city.status}
                chip
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Published Date */}
        <Box sx={{ mb: 3 }}>
          <InfoRow
            icon={<CalendarIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
            label="Published"
            value={city.published_at}
          />
        </Box>

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 2,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DescriptionIcon fontSize="small" />
            Description
          </Typography>

          <Card
            variant="outlined"
            sx={{
              border: `1px solid ${palette.grey[200]}`,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  fontFamily: 'poppinsRegular',
                  color: palette.text.primary,
                  lineHeight: 1.6,
                  fontSize: '14px',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontFamily: 'poppinsSemiBold',
                    marginTop: '12px',
                    marginBottom: '8px',
                    color: palette.text.primary
                  },
                  '& p': {
                    marginBottom: '12px'
                  },
                  '& strong': {
                    fontFamily: 'poppinsSemiBold'
                  },
                  '& ul, & ol': {
                    paddingLeft: '20px',
                    marginBottom: '12px'
                  },
                  '& li': {
                    marginBottom: '4px'
                  }
                }}
                dangerouslySetInnerHTML={renderHTML(city.description)}
              />
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Timestamps */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                color: palette.text.secondary,
                fontSize: '13px',
                mb: 0.5
              }}
            >
              Created At
            </Typography>
            <Typography
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary, fontSize: '14px' }}
            >
              {city.created_at ? formatDateTime(city.created_at) : 'N/A'}
            </Typography>
          </Box>
          {city.updated_at && city.updated_at !== city.created_at && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'poppinsMedium',
                  color: palette.text.secondary,
                  fontSize: '13px',
                  mb: 0.5
                }}
              >
                Last Updated
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.text.primary,
                  fontSize: '14px'
                }}
              >
                {formatDateTime(city.updated_at)}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.grey[200]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsSemiBold',
            px: 3,
            py: 1,
            borderRadius: 1,
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              backgroundColor: palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Close Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CityViewModal;
